// "Raise NCR in D365" (rule 29, migration 029): a short form, prefilled from the record it is raised
// from, that creates the NCR in D365 AS THE SIGNED-IN PERSON. The app keeps only the link; the NCR's
// approval and whether it is closed are read live from D365 each time (plan ruling 8).
//
// Hidden entirely while QUALITY_NCR_RAISE is off - and while the switch could not be read: a
// button that only ever answers "not switched on" is not worth showing. The button also needs
// RELEASE or RECEIVE in the register; the server checks again regardless.
//
// Rule 15: an NCR whose status could not be read from D365 reads "Status not known", never "open".
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Saved } from '@/components/Saved';
import { api, ApiError } from '../../lib/api';
import { fieldClass, labelClass, officeFocus, serverMessage, useResource, type MyGrants } from '../../shells/OfficeShell';

/**
 * Focus for an inline form a button opens (UI/UX audit finding 19). The form's first field takes
 * focus by `autoFocus` when it mounts; this hook gives back the focus to the button that opened
 * it when `open` goes from true to false (cancel, or done). Nothing happens on first render, so
 * a page never steals focus on load.
 */
export function useReturnFocus<T extends HTMLElement>(open: boolean) {
  const trigger = useRef<T>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!open && wasOpen.current) trigger.current?.focus();
    wasOpen.current = open;
  }, [open]);
  return trigger;
}

export type NcrSourceKind = 'INSPECTION_FAILURE' | 'COFA' | 'COMPLAINT';

export interface NcrConfig {
  enabled: boolean;
  problemTypes: string[];
}

export interface NcrLinkView {
  ncrId: string;
  batch: string | null;
  raisedByUpn: string;
  raisedAt: string;
  readBackOk: boolean;
  readBackNote: string | null;
  live: { found: boolean; approval?: string | null; closed?: boolean } | null;
  liveError: string | null;
}

/** One line of what the screen may prefill: an item, its supplier and the batches it was received into. */
export interface NcrLineOption {
  item: string;
  label: string;
  batches: string[];
}

export interface NcrPrefill {
  vendor: string | null;
  lines: NcrLineOption[];
  description: string;
}

/** What one linked NCR says now, in words. */
export function ncrStatusText(n: NcrLinkView): string {
  const head = `NCR ${n.ncrId} raised in D365 by ${n.raisedByUpn}${n.batch ? ` for batch ${n.batch}` : ''}`;
  const status = n.live
    ? n.live.found
      ? `${n.live.closed ? 'closed' : 'open'}, approval ${n.live.approval ?? 'not set'}`
      : 'not found in D365 now'
    : `status not known${n.liveError ? ` (${n.liveError.replace(/^Not known:\s*/, '')})` : ''}`;
  const check = n.readBackOk ? '' : ` D365 did not hold exactly what was sent: ${n.readBackNote ?? 'not read back'}.`;
  return `${head} · ${status}.${check}`;
}

function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Is raising switched on? Read once per page and handed to every panel on it. */
export function useNcrConfig(): NcrConfig | null {
  return useResource<NcrConfig>('/ncrs/config').data;
}

export function NcrPanel({
  config,
  source,
  prefill,
  grants,
}: {
  config: NcrConfig | null;
  source: { kind: NcrSourceKind; id: number };
  prefill: NcrPrefill;
  grants: MyGrants;
}) {
  const enabled = config?.enabled === true;
  const links = useResource<{ ncrs: NcrLinkView[] }>(enabled ? `/ncrs?sourceKind=${source.kind}&sourceId=${source.id}` : null);
  const [open, setOpen] = useState(false);
  // What the last raise recorded (audit finding 15): the status line below says what D365 holds.
  const [saved, setSaved] = useState<string | null>(null);
  const trigger = useReturnFocus<HTMLButtonElement>(open);
  if (!enabled) return null;
  const mayRaise = grants.known && (grants.holds('RELEASE') || grants.holds('RECEIVE'));
  return (
    <div className="mt-(--s2) flex flex-col gap-(--s1)" aria-label="D365 NCR">
      {saved ? <Saved>{saved}</Saved> : null}
      {links.error ? (
        <p className="m-0 text-(length:--fs-sm) text-(--warn)">
          The NCRs raised from here could not be read: {links.error}{' '}
          <Button variant="outline" type="button" onClick={links.reload}>
            Try again
          </Button>
        </p>
      ) : (
        (links.data?.ncrs ?? []).map((n) => (
          <p key={n.ncrId} className={`m-0 text-(length:--fs-sm) ${n.readBackOk ? '' : 'font-semibold text-(--warn)'}`}>
            {ncrStatusText(n)}
          </p>
        ))
      )}
      {mayRaise && !open ? (
        <Button
          ref={trigger}
          variant="outline"
          type="button"
          className="w-fit"
          onClick={() => {
            setSaved(null);
            setOpen(true);
          }}
        >
          Raise NCR in D365
        </Button>
      ) : null}
      {mayRaise && open ? (
        <NcrForm
          source={source}
          prefill={prefill}
          problemTypes={config?.problemTypes ?? []}
          onDone={(n) => {
            setSaved(`NCR ${n.ncrId} raised in D365${n.batch ? ` for batch ${n.batch}` : ''}.`);
            setOpen(false);
            links.reload();
          }}
          onCancel={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function NcrForm({
  source,
  prefill,
  problemTypes,
  onDone,
  onCancel,
}: {
  source: { kind: NcrSourceKind; id: number };
  prefill: NcrPrefill;
  problemTypes: string[];
  onDone: (n: NcrLinkView) => void;
  onCancel: () => void;
}) {
  const [key, setKey] = useState(newKey);
  const [lineIx, setLineIx] = useState(0);
  const line = prefill.lines[lineIx] ?? null;
  const [item, setItem] = useState(line?.item ?? '');
  const [batch, setBatch] = useState(line?.batches[0] ?? '');
  const [vendor, setVendor] = useState(prefill.vendor ?? '');
  const [problemType, setProblemType] = useState('');
  const [qty, setQty] = useState('0');
  const [description, setDescription] = useState(prefill.description);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickLine(ix: number) {
    setLineIx(ix);
    setItem(prefill.lines[ix]?.item ?? '');
    setBatch(prefill.lines[ix]?.batches[0] ?? '');
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ ncr: NcrLinkView; duplicate?: boolean }>('/ncrs', {
        clientKey: key,
        source,
        item,
        vendor,
        batch,
        qty: Number(qty),
        problemType,
        description,
      });
      onDone(res.ncr);
    } catch (err) {
      setError(serverMessage(err));
      // D365 said no: nothing was created, so the next press is a new raise. A lost answer keeps
      // the key, and the server refuses to raise it twice.
      const body = err instanceof ApiError ? (err.body as { newKeyNeeded?: boolean } | null) : null;
      if (body?.newKeyNeeded) setKey(newKey());
    } finally {
      setBusy(false);
    }
  }

  const ready = item.trim() !== '' && problemType !== '' && description.trim() !== '' && (source.kind !== 'COFA' || batch.trim() !== '');
  return (
    <div className="flex max-w-[36rem] flex-col gap-(--s2)">
      {prefill.lines.length > 1 ? (
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>PO line</span>
          <select autoFocus className={`${fieldClass} ${officeFocus}`} value={lineIx} onChange={(e) => pickLine(Number(e.target.value))}>
            {prefill.lines.map((l, i) => (
              <option key={`${l.item}-${i}`} value={i}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="flex flex-wrap gap-(--s2)">
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Item</span>
          {/* The first field takes focus when the form opens (finding 19), unless the PO line
              picker above it is the first. */}
          <input autoFocus={prefill.lines.length <= 1} className={`${fieldClass} ${officeFocus} font-mono`} value={item} onChange={(e) => setItem(e.target.value)} />
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Supplier (D365 vendor)</span>
          <input className={`${fieldClass} ${officeFocus} font-mono`} value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Batch{source.kind === 'COFA' ? '' : ' (optional)'}</span>
          <input className={`${fieldClass} ${officeFocus} font-mono`} value={batch} list="ncr-batches" onChange={(e) => setBatch(e.target.value)} />
          <datalist id="ncr-batches">
            {(line?.batches ?? []).map((b) => (
              <option key={b} value={b} />
            ))}
          </datalist>
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Defect qty</span>
          <input className={`${fieldClass} ${officeFocus} w-[8rem] tabular-nums`} inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </label>
      </div>
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>Problem type</span>
        <select className={`${fieldClass} ${officeFocus}`} value={problemType} onChange={(e) => setProblemType(e.target.value)}>
          <option value="">Choose</option>
          {problemTypes.map((p) => (
            <option key={p} value={p}>
              {p === 'Specification non-co' ? 'Specification non-conformance' : p}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>What was found (kept in the app: D365's NCR has no text field)</span>
        <textarea className={`${fieldClass} ${officeFocus}`} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <p className="m-0 text-(length:--fs-xs) text-(--muted)">
        Raised in D365 under your own sign-in, so your D365 security decides. Approval and closure stay in D365.
      </p>
      {error ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {error}
        </p>
      ) : null}
      <div className="flex gap-(--s2)">
        <Button type="button" disabled={busy || !ready} onClick={() => void submit()}>
          {busy ? 'Raising in D365…' : 'Raise NCR'}
        </Button>
        <Button variant="outline" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
